import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { notificationsApi } from '../services/api';
import { Megaphone, Send, Loader2, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const AnnouncementsPage = () => {
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [form, setForm] = useState({ title: '', message: '', type: 'announcement' });

  const fetchNotifications = async () => {
    try {
      const res = await notificationsApi.getAll();
      setNotifications(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchNotifications(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title || !form.message) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setLoading(true);
    try {
      await notificationsApi.create({ ...form, type: 'announcement' });
      toast.success('Annonce publiee avec succes');
      setForm({ title: '', message: '', type: 'announcement' });
      fetchNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationsApi.delete(id);
      toast.success('Annonce supprimee');
      fetchNotifications();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (d) => { try { return new Date(d).toLocaleString('fr-FR'); } catch { return d; } };

  return (
    <Layout>
      <div className="space-y-6" data-testid="announcements-page">
        <div>
          <h1 className="font-heading font-bold text-xl md:text-2xl text-slate-900">Annonces</h1>
          <p className="text-slate-500 text-sm mt-1">Publiez des annonces et notifications pour les utilisateurs</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-slate-200 shadow-sm rounded-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Megaphone className="w-5 h-5 text-[#0066FF]" />
                Nouvelle annonce
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label>Titre *</Label>
                  <Input placeholder="Titre de l'annonce" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required className="rounded-lg" data-testid="announce-title" />
                </div>
                <div className="space-y-2">
                  <Label>Contenu de l'annonce *</Label>
                  <Textarea placeholder="Redigez votre annonce ici. Vous pouvez ecrire un message detaille pour vos utilisateurs..." value={form.message} onChange={e => setForm({...form, message: e.target.value})} required className="rounded-lg min-h-[300px] resize-y text-sm leading-relaxed" data-testid="announce-message" />
                </div>
                <Button type="submit" className="w-full bg-[#0066FF] hover:bg-[#0052CC] rounded-lg" disabled={loading} data-testid="announce-publish-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Publier l'annonce
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm rounded-lg">
            <CardHeader>
              <CardTitle className="text-lg">Annonces publiees</CardTitle>
            </CardHeader>
            <CardContent>
              {notifications.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-50" strokeWidth={1} />
                  <p>Aucune annonce publiee</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {notifications.map((n) => (
                    <div key={n.id} className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-semibold text-slate-900 truncate">{n.title}</p>
                            <Badge className="bg-blue-100 text-blue-700 text-[10px] shrink-0">{n.type || 'Annonce'}</Badge>
                          </div>
                          <p className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-4">{n.message}</p>
                          <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                            <Clock className="w-3 h-3" />
                            {formatDate(n.created_at)}
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="shrink-0 text-slate-400 hover:text-red-500" onClick={() => handleDelete(n.id)} data-testid={`delete-announce-${n.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default AnnouncementsPage;
